<template>
  <div class="grid" :class="{ 'grid--empty': items.length === 0 }">
    <template v-if="items.length !== 0">
      <div v-if="singleMode" class="grid-col" style="width: 185px">
        <div class="grid-col-title">{{ $t("permissions_groups") }}</div>
        <div
          class="grid-col-group"
          :class="{ 'grid-col-group-single': singleMode }"
        >
          <div
            class="grid-col-group-row"
            :class="{
              'grid-col-group-row-single': singleMode,
              'grid-col-group-row--selected':
                singleMode && currentOwner === item.id,
            }"
            v-for="item in items.filter(
              (i) =>
                i.type === 'group' && (!i.children || i.children.length === 0),
            )"
            :key="item.id"
            @click="handleSelect(item.id)"
          >
            <app-checkbox
              v-if="!singleMode"
              :value="selected.includes(item.id)"
              @on-change="handleSelect(item.id)"
              @click.stop
            />
            <span
              class="grid-col-group-row-label"
              :class="{
                'grid-col-group-row-label--selected':
                  singleMode && currentOwner === item.id,
              }"
              v-html="highlightedName(item.label)"
            />
          </div>
        </div>
      </div>

      <div class="grid-col" style="width: 450px">
        <div class="grid-col-title">{{ $t("permissions_users") }}</div>
        <div class="grid-col-divisions">
          <div
            v-for="item in items.filter(
              (i) => i.type === 'group' && i.children?.length,
            )"
            :key="item.id"
            class="grid-col-divisions-division"
          >
            <div
              class="grid-col-divisions-division-row"
              @click="toggleGroup(item)"
            >
              <app-checkbox
                v-if="item.id !== -1 && !singleMode"
                :value="getGroupState(item).isSelected"
                @on-change="toggleGroup(item)"
                @click.stop
              />
              <div
                class="grid-col-divisions-division-row-label"
                v-html="highlightedName(item.label)"
              />
              <div class="grid-col-divisions-division-row-length">
                {{ item.children.length }}
              </div>
            </div>
            <div
              class="grid-col-divisions-division-user"
              :class="{
                'grid-col-divisions-division-user-single': singleMode,
              }"
            >
              <div
                v-for="child in item.children"
                :key="child.id"
                class="grid-col-divisions-division-user-row"
                :class="{
                  'grid-col-divisions-division-user-row-single': singleMode,
                  'grid-col-divisions-division-user-row--selected':
                    singleMode && currentOwner === child.id,
                }"
                @click="handleSelect(child.id)"
              >
                <app-checkbox
                  v-if="!singleMode"
                  :value="selected.includes(child.id)"
                  @on-change="handleSelect(child.id)"
                  @click.stop
                />
                <div
                  class="grid-col-divisions-division-user-row-label"
                  :class="{
                    'grid-col-divisions-division-user-row-label--selected':
                      singleMode && currentOwner === child.id,
                  }"
                  v-html="highlightedName(child.label)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!singleMode" class="grid-col" style="width: 205px">
        <div class="grid-col-title">{{ $t("permissions_groups") }}</div>
        <div class="grid-col-group">
          <div
            class="grid-col-group-row"
            style="gap: 8px"
            v-for="item in items.filter(
              (i) =>
                i.type === 'group' && (!i.children || i.children.length === 0),
            )"
            :key="item.id"
            @click="handleSelect(item.id)"
          >
            <app-checkbox
              v-if="!singleMode"
              :value="selected.includes(item.id)"
              @on-change="handleSelect(item.id)"
              @click.stop
            />
            <span
              class="grid-col-group-row-label"
              v-html="highlightedName(item.label)"
            />
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="grid-empty">{{ $t("permissions_users_not_found") }}</div>
    </template>
  </div>
</template>

<script setup lang="ts">
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import type {
  SelectGroup,
  SelectOption,
} from "@/components/ui-kit/select/AppSelect.vue";

const props = defineProps<{
  items: SelectOption[];
  selected: (string | number)[];
  select?: (id: string | number) => void;
  getGroupState: (group: SelectGroup) => {
    isSelected: boolean;
    isDisabled: boolean;
  };
  updateSelected?: (newSelected: (string | number)[]) => void;
  search?: string;
  singleMode?: boolean;
  close?: () => void;
  currentOwner?: number | null;
}>();

const handleSelect = (id: string | number) => {
  if (props.updateSelected) {
    const isAlreadySelected = props.selected.includes(id);
    const newSelected = isAlreadySelected
      ? props.selected.filter((s) => s !== id)
      : [...props.selected, id];
    props.updateSelected(newSelected);
  } else if (typeof props.select === "function") {
    props.select(id);
  }

  if (props.singleMode && props.close) {
    props.close();
  }
};

const toggleGroup = (group: SelectGroup) => {
  if (!group.children?.length) return;
  const childIds = group.children.map((child) => child.id);
  const allSelected = childIds.every((id) => props.selected.includes(id));

  if (props.updateSelected) {
    const newSelected = allSelected
      ? props.selected.filter((id) => !childIds.includes(id))
      : [
          ...props.selected,
          ...childIds.filter((id) => !props.selected.includes(id)),
        ];
    props.updateSelected(newSelected);
  } else {
    childIds.forEach((id) => {
      if (typeof props.select !== "function") return;
      if (allSelected && props.selected.includes(id)) props.select(id);
      else if (!allSelected && !props.selected.includes(id)) props.select(id);
    });
  }
};

const escapeHtml = (text: string): string => {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
};

const highlightedName = (label: string): string => {
  const searchTerm = props.search?.trim();
  if (!searchTerm) {
    return escapeHtml(label);
  }

  const lowerLabel = label.toLowerCase();
  const lowerSearch = searchTerm.toLowerCase();
  const startIndex = lowerLabel.indexOf(lowerSearch);

  if (startIndex === -1) {
    return escapeHtml(label);
  }

  const endIndex = startIndex + searchTerm.length;
  const beforeMatch = label.substring(0, startIndex);
  const match = label.substring(startIndex, endIndex);
  const afterMatch = label.substring(endIndex);

  return `${escapeHtml(
    beforeMatch,
  )}<span style="background-color:#D3E3FD;border-radius:4px;padding:1px;">${escapeHtml(
    match,
  )}</span>${escapeHtml(afterMatch)}`;
};
</script>

<style scoped lang="scss">
@import "@/styles/color.scss";

.grid {
  display: inline-flex;
  position: relative;
  padding: 0px 12px 16px;
  gap: 16px;
  width: 100%;

  &-col {
    display: inline-flex;
    flex-direction: column;
    gap: 8px;

    &-title {
      color: $color-text-secondary;
      font-size: 12px;
      font-style: normal;
      font-weight: 600;
      line-height: 16px;
    }

    &-divisions {
      gap: 16px;
      display: flex;
      flex-direction: column;

      &-division {
        gap: 8px;
        display: flex;
        flex-direction: column;

        &-user {
          display: inline-grid;
          grid-template-columns: repeat(2, 1fr);

          padding-left: 8px;
          column-gap: 16px;
          row-gap: 8px;

          &-single {
            padding-left: 0px;
            column-gap: 0px;
            row-gap: 4px;
          }

          &-row {
            display: flex;
            height: fit-content;
            padding: 0px 8px 0px 16px;
            border-radius: 8px;
            flex-direction: row;
            gap: 8px;
            cursor: pointer;

            &-single {
              padding: 3px 8px 3px 16px;
              min-height: 28px
            }

            &-single {
              &:hover {
                background-color: $color-divider;
              }
            }

            &:hover {
              :deep(.app-checkbox__item-label) {
                background-color: $color-divider;
              }
            }

            &--selected {
              &:hover {
                background-color: #f3f8f4 !important;
              }
            }

            &-label {
              align-self: center;
              color: $color-text_main;
              font-size: 14px;
              font-style: normal;
              font-weight: 400;
              line-height: 18px;
              cursor: pointer;

              &--selected {
                color: #3f884f !important;
              }
            }
          }
        }

        &-row {
          display: flex;
          flex-direction: row;
          gap: 8px;

          &-checkbox {
            align-self: center;
          }

          &-length {
            flex: none;
            align-self: flex-start;
            white-space: nowrap;
            font-size: 12px;
            font-style: normal;
            font-weight: 400;
            line-height: 14px;
            text-transform: lowercase;
            color: $color-text-secondary;

            display: flex;
            padding: 2px 6px;
            justify-content: center;
            align-items: center;
            border-radius: 8px;
            background: $color-divider;
          }

          &:hover {
            :deep(.app-checkbox__item-label) {
              background-color: $color-divider;
            }
          }

          &-label {
            color: $color-text_main;
            font-size: 14px;
            font-style: normal;
            font-weight: 600;
            line-height: 18px;
          }
        }
      }
    }

    &-group {
      display: inline-flex;
      flex-direction: column;
      gap: 8px;

      &-single {
        gap: 0;
      }

      &-row {
        display: flex;
        flex-direction: row;
        padding: 0px 8px 0px 0px;
        border-radius: 8px;
        gap: 8px;
        cursor: pointer;

        &-single {
          gap: 0;
          padding: 3px 8px 3px 16px;
          min-height: 28px;

          &:hover {
            background-color: $color-divider;
          }
        }

        &--selected {
          &:hover {
            background-color: #f3f8f4 !important;
          }
        }

        &:hover {
          :deep(.app-checkbox__item-label) {
            background-color: $color-divider;
          }
        }

        &-label {
          color: $color-text_main;
          align-self: center;
          font-size: 14px;
          font-style: normal;
          font-weight: 400;
          line-height: 18px;

          &--selected {
            color: #3f884f !important;
          }
        }
      }
    }
  }

  &-empty {
    text-align: center;
    color: $color-text_main;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    margin-top: 48px;
  }

  &--empty {
    display: flex;
    justify-content: center;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    width: 100%;
    padding: 0 12px 16px;
    box-sizing: border-box;
    overflow-x: hidden;

    &-col {
      width: auto !important;
      min-width: 0;

      &-divisions-division {
        min-width: 0;

        &-user {
          grid-template-columns: minmax(0, 1fr);
          min-width: 0;
          padding-left: 0;
          column-gap: 0;

          &-row {
            min-width: 0;
            padding-right: 0;
            padding-left: 0;

            &-label {
              min-width: 0;
              overflow-wrap: anywhere;
            }
          }
        }

        &-row {
          min-width: 0;

          &-label {
            min-width: 0;
            overflow-wrap: anywhere;
          }
        }
      }

      &-group-row {
        min-width: 0;
        padding-right: 0;

        &-label {
          min-width: 0;
          overflow-wrap: anywhere;
        }
      }
    }
  }
}
</style>
